import { merge } from 'lodash';
import nextLocaleTextEn from '@meitu/x-design-locales-next/en.json';
import type { NextLocaleText } from '@meitu/x-design-locales-next/types';
import nextLocaleTextCn from '@meitu/x-design-locales-next/zh-Hans.json';
import {
  createLocaleTextWithDevI18nKey,
  getLocale,
  setLocale,
  useLocaleText,
  type LocaleTextPrinter,
} from '@meitu/xdesign-i18n';

export type LocaleText = NextLocaleText;

export const localeTextMap: Record<string, LocaleText> = {
  'zh-Hans': merge({}, nextLocaleTextCn),
  en: merge({}, nextLocaleTextEn),
};

export type LocaleCode = keyof typeof localeTextMap;

export const SUPPORT_LOCALES = Object.keys(localeTextMap);

function mergeLocaleText(
  locale: LocaleCode,
  fallback: LocaleText,
  devNextOverrides: Record<string, unknown>
) {
  localeTextMap[locale] = merge(
    {},
    fallback,
    window.__LOCALE_TEXT_NEXT_MAP__ ?? {},
    devNextOverrides
  ) as LocaleText;
}

if (typeof window !== 'undefined' && window.__LOCALE__) {
  const loc = window.__LOCALE__;
  const fallback = localeTextMap[loc] ?? localeTextMap['zh-Hans'];
  const devNextOverrides = window.__DEV_NEXT_LOCALE_OVERRIDES__ ?? {};
  mergeLocaleText(loc, fallback, devNextOverrides);
}

type LocaleStore = {
  locale: LocaleCode;
  $t: LocaleTextPrinter<LocaleText>;
};

const localStoreCache: Partial<Record<LocaleCode, LocaleStore>> = {};

export const initLocaleStore = (locale: LocaleCode) => {
  setLocale(locale);
};

export const getLocaleStore = (_locale?: LocaleCode) => {
  const locale: LocaleCode = _locale || getLocale() || 'zh-Hans';
  const localeText = localeTextMap[locale];

  if (!localStoreCache[locale]) {
    localStoreCache[locale] = {
      locale,
      $t: createLocaleTextWithDevI18nKey(
        localeText as unknown as Record<string, unknown>
      ) as unknown as LocaleTextPrinter<LocaleText>,
    };
  }

  return localStoreCache[locale]!;
};

export const useLocaleStore = () => {
  const locale = useLocaleText<LocaleCode>();

  return getLocaleStore(locale);
};
